import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, Rewind, FastForward, X, Headphones, ChevronDown, ChevronRight, Lock, TrendingUp, Flag, BookText, Landmark, Lightbulb, Heart, Gavel, Users, BookOpen } from 'lucide-react';
import { AudioChapter } from '../types';
import { audiobookChapters } from '../data/audiobookChapters';
import { topics, chaptersIndex } from '../data/chaptersIndex';
import { saveAudioProgress, getChapterProgress, getAllAudioProgress } from '../utils/firebaseStorage';
import { useStreak } from '../contexts/StreakContext';
import { useEntitlements } from '../hooks/useEntitlements';
import { useAuth } from '../AuthContext';
import { useToast } from '../contexts/ToastContext';
import { createNetworkError, createAuthError, createCheckoutError, createTokenError, parseErrorMessage, createRetryableAction } from '../utils/errorHandling';
import { LoadingButtonContent } from './LoadingComponents';

interface AudiobooksSectionProps {
  className?: string;
  autoPlayChapter?: string;
  onNavigate?: (section: string) => void;
}

interface CheckoutRequest {
  priceId: string;
  productId: 'pro';
  mode: 'payment';
}

interface CheckoutResponse {
  url: string;
}

const AudiobooksSection: React.FC<AudiobooksSectionProps> = ({ className = '', autoPlayChapter, onNavigate }) => {
  // Streak tracking
  const { trackActivity } = useStreak();

  useEffect(() => {
    console.log('AudiobooksSection mounted - enforcing PRO badge update');
  }, []);

  // Pro gate functionality
  const { user, hasPro } = useEntitlements();
  const { signInWithGoogle } = useAuth();
  const { showError, showSuccess, showInfo } = useToast();
  const [showProModal, setShowProModal] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State management hooks for audio playback
  const [currentAudio, setCurrentAudio] = useState<AudioChapter | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState(0); // Track drag position separately
  const [wasPlayingBeforeDrag, setWasPlayingBeforeDrag] = useState(false);
  const [completedChapters, setCompletedChapters] = useState<Set<string>>(new Set()); // Track completed chapters for XP
  const [allChapterProgress, setAllChapterProgress] = useState<{ [chapterId: string]: any }>({});

  // State for topic expansion - start with all topics collapsed
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());

  // Check if a chapter is free
  const isChapterFree = useCallback((chapterId: string) => {
    // Define free chapters
    const freeChapterIds = [
      'etika-moralka',           // Philosophy - Ethics and morality
      'filosoficke-smery',       // Philosophy - Basic philosophical directions
      'ustavni-pravo-principy'   // Law - Constitutional law
    ];
    return freeChapterIds.includes(chapterId);
  }, []);

  // Check if a topic has any free chapters
  const topicHasFreeChapters = (topicId: string) => {
    const topicChapters = chaptersIndex.filter(chapter => chapter.topic === topicId);
    return topicChapters.some(chapter => isChapterFree(chapter.id));
  };

  // Ref for the hidden HTML5 audio element
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // Toggle topic expansion
  const toggleTopic = (topicId: string) => {
    const newExpanded = new Set(expandedTopics);
    if (newExpanded.has(topicId)) {
      newExpanded.delete(topicId);
    } else {
      newExpanded.add(topicId);
    }
    setExpandedTopics(newExpanded);
  };

  // Map topic IDs to lucide-react icons (same as flashcards/courses)
  const getTopicIcon = (topicId: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      'ekonomie': <TrendingUp className="w-5 h-5" />,
      'evropska-integrace': <Flag className="w-5 h-5" />,
      'moderni-historie': <BookText className="w-5 h-5" />,
      'politologie': <Landmark className="w-5 h-5" />,
      'filosofie': <Lightbulb className="w-5 h-5" />,
      'psychologie': <Heart className="w-5 h-5" />,
      'pravo': <Gavel className="w-5 h-5" />,
      'sociologie': <Users className="w-5 h-5" />
    };
    return iconMap[topicId] || <BookOpen className="w-5 h-5" />;
  };

  // Get icon color based on topic (same as flashcards/courses)
  const getIconColor = (topicId: string) => {
    const colorMap: { [key: string]: string } = {
      'ekonomie': 'text-green-600 dark:text-green-400',
      'evropska-integrace': 'text-blue-600 dark:text-blue-400',
      'moderni-historie': 'text-amber-600 dark:text-amber-400',
      'politologie': 'text-purple-600 dark:text-purple-400',
      'filosofie': 'text-indigo-600 dark:text-indigo-400',
      'psychologie': 'text-pink-600 dark:text-pink-400',
      'pravo': 'text-orange-600 dark:text-orange-400',
      'sociologie': 'text-teal-600 dark:text-teal-400'
    };
    return colorMap[topicId] || 'text-gray-600 dark:text-gray-400';
  };

  // Get background color for icon circle (same as flashcards/courses)
  const getIconBgColor = (topicId: string) => {
    const bgMap: { [key: string]: string } = {
      'ekonomie': 'bg-green-100 dark:bg-green-900',
      'evropska-integrace': 'bg-blue-100 dark:bg-blue-900',
      'moderni-historie': 'bg-amber-100 dark:bg-amber-900',
      'politologie': 'bg-purple-100 dark:bg-purple-900',
      'filosofie': 'bg-indigo-100 dark:bg-indigo-900',
      'psychologie': 'bg-pink-100 dark:bg-pink-900',
      'pravo': 'bg-orange-100 dark:bg-orange-900',
      'sociologie': 'bg-teal-100 dark:bg-teal-900'
    };
    return bgMap[topicId] || 'bg-gray-100 dark:bg-gray-700';
  };

  // Create retryable checkout action
  const performCheckout = createRetryableAction(async (idToken: string) => {
    const checkoutRequest: CheckoutRequest = {
      priceId: 'price_1S81xhCR1GWecDhI7CNL2ELF', // Your Stripe price ID
      productId: 'pro',
      mode: 'payment'
    };

    const response = await fetch('/.netlify/functions/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify(checkoutRequest)
    });

    if (!response.ok) {
      if (response.status >= 500) {
        throw createNetworkError('Server je dočasně nedostupný. Zkus to za chvilku.');
      } else if (response.status === 401) {
        throw createTokenError('Přihlas se prosím znovu.');
      } else {
        throw createCheckoutError(`Checkout selhal (${response.status}). Zkus to znovu.`);
      }
    }

    return response.json();
  }, 2, 1500);

  // Handle checkout flow for signed-in users
  const handleCheckout = async () => {
    if (!user) {
      try {
        showInfo('Přihlašování...', 'Přesměrováváme tě na přihlášení přes Google.');
        await signInWithGoogle();
        showSuccess('Přihlášení úspěšné!', 'Nyní můžeš pokračovat k nákupu.');
        return;
      } catch (error) {
        console.error('Sign-in failed:', error);
        const errorInfo = parseErrorMessage(createAuthError('Přihlášení se nezdařilo. Zkus to prosím znovu.'));
        setError(errorInfo.message);
        showError(errorInfo.title, errorInfo.message);
        return;
      }
    }

    setCheckoutLoading(true);
    setError(null);

    try {
      showInfo('Připravujeme checkout...', 'Chvilku strpení, otevíráme platební bránu.');

      const idToken = await user.getIdToken();
      const data: CheckoutResponse = await performCheckout(idToken);

      showSuccess('Přesměrování na platbu', 'Otevíráme Stripe checkout...');

      setTimeout(() => {
        window.location.href = data.url;
      }, 1000);

    } catch (error) {
      console.error('Checkout error:', error);
      const errorInfo = parseErrorMessage(error);
      setError(errorInfo.message);
      showError(errorInfo.title, errorInfo.message);
      setCheckoutLoading(false);
    }
  };

  // Handle sign-in for signed-out users
  const handleSignIn = async () => {
    try {
      showInfo('Přihlašování...', 'Přesměrováváme tě na přihlášení přes Google.');
      await signInWithGoogle();
      showSuccess('Přihlášení úspěšné!', 'Vítej zpět! Nyní máš přístup ke svému účtu.');
    } catch (error) {
      console.error('Sign-in failed:', error);
      const errorInfo = parseErrorMessage(createAuthError('Přihlášení se nezdařilo. Zkus to prosím znovu.'));
      setError(errorInfo.message);
      showError(errorInfo.title, errorInfo.message);
    }
  };

  // Group chapters by topic
  const chaptersByTopic = topics.map(topic => ({
    ...topic,
    chapters: chaptersIndex
      .filter(chapter => chapter.topic === topic.id)
      .map(chapter => audiobookChapters.find(audio => audio.id === chapter.id))
      .filter(Boolean) as AudioChapter[]
  })).filter(topic => topic.chapters.length > 0);

  // Handle chapter selection
  const handleChapterSelect = useCallback(async (chapter: AudioChapter) => {
    // Check if chapter is free or user has Pro
    if (!isChapterFree(chapter.id) && !hasPro) {
      onNavigate?.('upgrade');
      return;
    }

    setCurrentAudio(chapter);
    setIsPlaying(false);

    // Load saved progress for this chapter
    const savedProgress = await getChapterProgress(chapter.id);
    const startTime = savedProgress?.currentTime || 0;
    setCurrentTime(startTime);

    if (audioRef.current) {
      audioRef.current.src = chapter.src;
      audioRef.current.load();

      // Auto-play when metadata is loaded
      const handleCanPlay = () => {
        // Restore saved position
        if (startTime > 0) {
          audioRef.current!.currentTime = startTime;
        }

        audioRef.current?.play().then(() => {
          setIsPlaying(true);
        }).catch((error) => {
          console.error('Auto-play failed:', error);
          // Auto-play might be blocked by browser, user will need to click play
        });
        audioRef.current?.removeEventListener('canplay', handleCanPlay);
      };

      audioRef.current.addEventListener('canplay', handleCanPlay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPro, isChapterFree, onNavigate]);

  // Load all chapter progress on component mount
  useEffect(() => {
    const loadAllProgress = async () => {
      try {
        const progress = await getAllAudioProgress();
        setAllChapterProgress(progress);

        // Update completed chapters set
        const completed = new Set<string>();
        Object.entries(progress).forEach(([chapterId, chapterProgress]) => {
          if (chapterProgress.isCompleted) {
            completed.add(chapterId);
          }
        });
        setCompletedChapters(completed);
      } catch (error) {
        console.error('Error loading chapter progress:', error);
      }
    };

    loadAllProgress();
  }, []);

  // Auto-play chapter when component loads
  useEffect(() => {
    if (autoPlayChapter) {
      const chapterToPlay = audiobookChapters.find(ch => ch.id === autoPlayChapter);
      if (chapterToPlay) {
        // Find which topic this chapter belongs to and expand it
        const chapterIndex = chaptersIndex.find(ch => ch.id === autoPlayChapter);
        if (chapterIndex) {
          setExpandedTopics(new Set([chapterIndex.topic]));
        }

        // Auto-select and play the chapter
        handleChapterSelect(chapterToPlay);
      }
    }
  }, [autoPlayChapter, handleChapterSelect]);

  // Load most recent chapter on component mount (when no autoPlayChapter is specified)
  useEffect(() => {
    if (!autoPlayChapter) {
      const loadMostRecentChapter = async () => {
        try {
          const allAudioProgress = await getAllAudioProgress();
          const chapters = Object.values(allAudioProgress);

          if (chapters.length > 0) {
            // Find the most recently played chapter
            const mostRecent = chapters.reduce((mostRecent, current) => {
              const currentTime = new Date(current.lastPlayed).getTime();
              const mostRecentTime = new Date(mostRecent.lastPlayed).getTime();
              return currentTime > mostRecentTime ? current : mostRecent;
            });

            // Find which topic this chapter belongs to and expand it
            const chapterIndex = chaptersIndex.find(ch => ch.id === mostRecent.chapterId);
            if (chapterIndex) {
              setExpandedTopics(new Set([chapterIndex.topic]));
            }
          }
        } catch (error) {
          console.error('Error loading recent chapter:', error);
        }
      };

      loadMostRecentChapter();
    }
  }, [autoPlayChapter]);



  // Handle closing playback bar
  const handleClosePlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setCurrentAudio(null);
    setIsPlaying(false);
    setCurrentTime(0);
  };

  // Handle play/pause toggle
  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  // Handle 10-second rewind
  const handleRewind = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  // Handle 10-second forward
  const handleFastForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 10);
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  // Handle timeline seek
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (audioRef.current && duration > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const newTime = (clickX / rect.width) * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Handle drag start (mouse)
  const handleDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Drag started (mouse)');

    // Store current playing state and pause audio for responsive dragging
    setWasPlayingBeforeDrag(isPlaying);
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    }

    // Initialize drag position with current progress position
    setDragPosition(progressPercentage);
    setIsDragging(true);
  };

  // Handle touch start (mobile)
  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Drag started (touch)');

    // Store current playing state and pause audio for responsive dragging
    setWasPlayingBeforeDrag(isPlaying);
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    }

    // Initialize drag position with current progress position
    setDragPosition(progressPercentage);
    setIsDragging(true);
  };



  // (Removed unused handleTouchSeek to satisfy ESLint no-unused-vars)

  // Set up drag event listeners
  useEffect(() => {
    const dragMove = (e: MouseEvent) => {
      if (!isDragging || !progressBarRef.current || duration === 0) return;

      const rect = progressBarRef.current.getBoundingClientRect();
      const clickX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      const newPosition = (clickX / rect.width) * 100;
      const newTime = (clickX / rect.width) * duration;

      // Update visual position immediately - ultra responsive!
      setDragPosition(newPosition);
      setCurrentTime(newTime); // Update time display only

      // Don't seek audio during drag - just update visuals for maximum responsiveness
    };

    const touchMove = (e: TouchEvent) => {
      if (!isDragging || !progressBarRef.current || duration === 0) return;

      e.preventDefault();
      const touch = e.touches[0];
      const rect = progressBarRef.current.getBoundingClientRect();
      const clickX = Math.max(0, Math.min(touch.clientX - rect.left, rect.width));
      const newPosition = (clickX / rect.width) * 100;
      const newTime = (clickX / rect.width) * duration;

      // Update visual position immediately - ultra responsive!
      setDragPosition(newPosition);
      setCurrentTime(newTime); // Update time display only

      // Don't seek audio during drag - just update visuals for maximum responsiveness
    };

    const dragEnd = () => {
      console.log('Drag ended');
      setIsDragging(false);

      // Now seek to the final position and resume playback if it was playing before
      if (audioRef.current && duration > 0) {
        const finalTime = (dragPosition / 100) * duration;
        audioRef.current.currentTime = finalTime;
        setCurrentTime(finalTime);

        // Resume playback if it was playing before drag
        if (wasPlayingBeforeDrag) {
          audioRef.current.play().then(() => {
            setIsPlaying(true);
          }).catch((error) => {
            console.error('Failed to resume playback after drag:', error);
          });
        }
      }

      setWasPlayingBeforeDrag(false);
    };

    if (isDragging) {
      console.log('Setting up drag listeners');
      // Mouse events
      document.addEventListener('mousemove', dragMove);
      document.addEventListener('mouseup', dragEnd);

      // Touch events
      document.addEventListener('touchmove', touchMove, { passive: false });
      document.addEventListener('touchend', dragEnd);

      return () => {
        console.log('Cleaning up drag listeners');
        document.removeEventListener('mousemove', dragMove);
        document.removeEventListener('mouseup', dragEnd);
        document.removeEventListener('touchmove', touchMove);
        document.removeEventListener('touchend', dragEnd);
      };
    }
  }, [isDragging, duration, dragPosition, wasPlayingBeforeDrag]);

  // Format time in MM:SS format
  const formatTime = (seconds: number): string => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate progress percentage
  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Tracking refs for streak system
  const listenedSecondsRef = useRef(0);
  const lastTimeRef = useRef(0);
  const streakTrackedRef = useRef(false);

  // Set up audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Reset tracking on new audio load
    listenedSecondsRef.current = 0;
    streakTrackedRef.current = false;
    lastTimeRef.current = audio.currentTime;

    // Handle metadata loaded (duration available)
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    // Handle time updates during playback
    const handleTimeUpdate = () => {
      const now = audio.currentTime;
      const timeDiff = now - lastTimeRef.current;

      // Update last time for next tick
      lastTimeRef.current = now;

      // Only accumulate valid playback time (ignore seeks/pauses which cause large jumps or negative diffs)
      // Logic: standard update is usually ~0.25s. Allow up to 1.5s for lag.
      if (timeDiff > 0 && timeDiff < 1.5 && isPlaying) {
        listenedSecondsRef.current += timeDiff;

        // Check for streak threshold (120 seconds = 2 minutes)
        if (listenedSecondsRef.current >= 120 && !streakTrackedRef.current) {
          console.log('🎧 Audiobook Streak Threshold Reached: 2 minutes listened!');
          trackActivity('audiobook_listened', 2);
          streakTrackedRef.current = true; // Only track once per session/chapter load

          // Optional: Show toast or visual feedback? 
          // Ideally the streak widget updates automatically via context events
        }
      } else {
        // If jump detected (seek), don't add to accumulated time
        // console.log('Seek detected or paused, skipping accumulation');
      }

      setCurrentTime(now);

      // Save progress every few seconds
      if (currentAudio && audio.duration > 0) {
        const isCompleted = audio.currentTime >= audio.duration - 5; // Consider completed if within 5 seconds of end

        saveAudioProgress(currentAudio.id, {
          currentTime: audio.currentTime,
          duration: audio.duration,
          lastPlayed: new Date().toISOString(),
          isCompleted
        }).then(() => {
          // Update local progress state
          setAllChapterProgress(prev => ({
            ...prev,
            [currentAudio.id]: {
              chapterId: currentAudio.id,
              currentTime: audio.currentTime,
              duration: audio.duration,
              lastPlayed: new Date().toISOString(),
              isCompleted
            }
          }));
        }).catch(error => console.error('Failed to save audio progress:', error));

        // Award XP and track streak activity when chapter is completed (only once per chapter)
        if (isCompleted && !completedChapters.has(currentAudio.id)) {
          console.log('Chapter completed:', currentAudio.id, currentAudio.title);
          setCompletedChapters(prev => new Set([...Array.from(prev), currentAudio.id]));

          // Legacy check for completion-based activity (optional, but keeping for completeness)
          // const minutesListened = Math.ceil(audio.duration / 60);
          // trackActivity('audiobook_listened', minutesListened);
        }
      }
    };

    // Handle audio end
    const handleEnded = () => {
      setIsPlaying(false);
      // Don't reset time - keep it at the end to show completion
      setCurrentTime(audio.duration);
      // Don't reset audio.currentTime - this preserves the completion state
    };

    // Handle audio errors
    const handleError = () => {
      console.error('Audio playback error');
      setIsPlaying(false);
    };

    // Add event listeners
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    // Cleanup function to remove event listeners
    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [currentAudio, completedChapters, trackActivity, isPlaying]); // Re-run when currentAudio or isPlaying changes

  return (
    <div className={`audiobooks-section ${className}`}>
      {/* Hidden HTML5 audio element for playback control */}
      <audio
        ref={audioRef}
        style={{ display: 'none' }}
        preload="metadata"
      />

      {/* Main content area */}
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Audiokniha
          </h2>
          {chaptersByTopic.length > 0 && (
            <button
              onClick={() => {
                if (expandedTopics.size > 0) {
                  setExpandedTopics(new Set());
                } else {
                  setExpandedTopics(new Set(chaptersByTopic.map(t => t.id)));
                }
              }}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
            >
              {expandedTopics.size > 0 ? 'Sbalit vše' : 'Rozbalit vše'}
            </button>
          )}
        </div>

        {/* Topics and chapters */}
        {chaptersByTopic.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              Žádné audio kapitoly nejsou k dispozici
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {chaptersByTopic.map((topic) => {
              const hasFreeChapters = topicHasFreeChapters(topic.id);
              const freeChaptersCount = topic.chapters.filter(chapter => isChapterFree(chapter.id)).length;
              const totalChapters = topic.chapters.length;
              const isFullyLocked = !hasFreeChapters && !hasPro;

              return (
                <div
                  key={topic.id}
                  className={`border border-border rounded-lg overflow-hidden bg-card transition-all duration-200`}
                >
                  {/* Topic header */}
                  <button
                    onClick={() => {
                      if (isFullyLocked) {
                        onNavigate?.('upgrade');
                      } else {
                        toggleTopic(topic.id);
                      }
                    }}
                    className="w-full flex items-center justify-between p-4 hover:bg-accent transition-colors duration-200"
                  >
                    <div className="flex items-center space-x-3">
                      {/* Icon circle instead of emoji */}
                      <div className={`
                        w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0
                        ${getIconBgColor(topic.id)}
                      `}>
                        <div className={getIconColor(topic.id)}>
                          {getTopicIcon(topic.id)}
                        </div>
                      </div>
                      <div className="text-left">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                          {topic.title}
                          {hasFreeChapters && !hasPro && (
                            <span className="ml-2 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
                              {freeChaptersCount} ZDARMA
                            </span>
                          )}
                          {hasFreeChapters && hasPro && (
                            <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full">
                              PRO
                            </span>
                          )}
                          {isFullyLocked && (
                            <div className="ml-2 bg-yellow-400 text-yellow-950 text-xs font-black tracking-wide px-2 py-1 rounded-md shadow-sm border-b-2 border-yellow-600 flex items-center">
                              PRO
                            </div>
                          )}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {topic.description} • {totalChapters} kapitol
                          {hasFreeChapters && !hasPro && freeChaptersCount < totalChapters && (
                            <span className="text-green-600 dark:text-green-400"> • {freeChaptersCount} zdarma</span>
                          )}
                        </p>
                      </div>
                    </div>
                    {isFullyLocked ? (
                      null
                    ) : expandedTopics.has(topic.id) ? (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-500" />
                    )}
                  </button>

                  {/* Chapters list - show if not fully locked */}
                  {!isFullyLocked && expandedTopics.has(topic.id) && (
                    <div className="bg-background border-t border-border">
                      {topic.chapters.map((chapter, index) => {
                        const isSelected = currentAudio?.id === chapter.id;
                        const chapterProgress = allChapterProgress[chapter.id];
                        const isCompleted = chapterProgress?.isCompleted || false;
                        const chapterIsFree = isChapterFree(chapter.id);
                        const chapterIsLocked = !chapterIsFree && !hasPro;

                        return (
                          <div
                            key={chapter.id}
                            onClick={() => handleChapterSelect(chapter)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleChapterSelect(chapter);
                              }
                            }}
                            tabIndex={0}
                            role="button"
                            aria-label={`Select audio chapter: ${chapter.title}`}
                            aria-pressed={isSelected}
                            className={`flex items-center p-4 transition-all duration-200 cursor-pointer group focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset ${index !== topic.chapters.length - 1 ? 'border-b border-gray-100 dark:border-gray-800' : ''
                              } ${isSelected
                                ? 'bg-blue-50 dark:bg-blue-900/20'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                              }`}
                          >
                            {/* Play icon */}
                            <div className="flex-shrink-0 mr-4">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-200 ${chapterIsLocked
                                ? 'bg-gray-100 dark:bg-gray-700'
                                : isCompleted
                                  ? 'bg-green-100 dark:bg-green-900 group-hover:bg-green-200 dark:group-hover:bg-green-800'
                                  : 'bg-blue-100 dark:bg-blue-900 group-hover:bg-blue-200 dark:group-hover:bg-blue-800'
                                }`}>
                                {chapterIsLocked ? (
                                  <Lock className="w-5 h-5 text-gray-500" />
                                ) : isCompleted ? (
                                  <span className="text-green-600 dark:text-green-400 text-sm font-bold">✓</span>
                                ) : (
                                  <Play className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                )}
                              </div>
                            </div>

                            {/* Chapter title and progress */}
                            <div className="flex-1 min-w-0">
                              <h4 className={`text-base font-medium truncate flex items-center ${chapterIsLocked
                                ? 'text-gray-500 dark:text-gray-400'
                                : isCompleted
                                  ? 'text-green-700 dark:text-green-300'
                                  : 'text-gray-900 dark:text-white'
                                }`}>
                                {chapter.title}
                                {chapterIsFree && !hasPro && (
                                  <span className="ml-2 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded">
                                    ZDARMA
                                  </span>
                                )}
                                {chapterIsLocked && (
                                  <div className="ml-2 bg-yellow-400 text-yellow-950 text-xs font-black tracking-wide px-2 py-0.5 rounded-md shadow-sm border-b-2 border-yellow-600 flex items-center">
                                    PRO
                                  </div>
                                )}
                                {isCompleted && <span className="ml-2 text-green-600 dark:text-green-400">✓</span>}
                              </h4>
                              {isSelected && duration > 0 && !chapterIsLocked && (
                                <div className="mt-2">
                                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    <span>{formatTime(currentTime)}</span>
                                    <span>{formatTime(duration)}</span>
                                  </div>
                                  <div className="w-full h-1 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-blue-500 dark:bg-blue-400 transition-all duration-300"
                                      style={{ width: `${progressPercentage}%` }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Playback bar - fixed at bottom */}
      {currentAudio && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg z-50">
          {/* Simple Timeline Progress Bar */}
          <div className="w-full px-4 py-2">
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>

            {/* Progress bar container */}
            <div
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full cursor-pointer relative"
              onClick={handleSeek}
              ref={progressBarRef}
            >
              {/* Progress fill */}
              <div
                className="h-full bg-blue-500 dark:bg-blue-400 rounded-full transition-all duration-200"
                style={{ width: `${progressPercentage}%` }}
              />

              {/* Draggable thumb */}
              <div
                className={`absolute top-1/2 w-4 h-4 rounded-full transform -translate-y-1/2 cursor-grab shadow-md border-2 transition-all duration-200 ${isDragging
                  ? 'bg-red-500 border-red-600 scale-110'
                  : 'bg-white border-blue-500'
                  }`}
                style={{
                  left: `${isDragging ? dragPosition : progressPercentage}%`,
                  marginLeft: '-8px'
                }}
                onMouseDown={handleDragStart}
                onTouchStart={handleTouchStart}
              />
            </div>
          </div>

          <div className="flex items-center justify-between p-4 max-w-7xl mx-auto">
            {/* Current chapter info */}
            <div className="flex-1 min-w-0 mr-4">
              <div className="flex items-center space-x-2">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <Headphones className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                    {currentAudio.title}
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </p>
                </div>
              </div>
            </div>

            {/* Playback controls */}
            <div className="flex items-center space-x-2 mr-4">
              {/* Rewind button */}
              <button
                onClick={handleRewind}
                className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Rewind 10 seconds"
              >
                <Rewind className="w-5 h-5" />
              </button>

              {/* Play/Pause button */}
              <button
                onClick={handlePlayPause}
                className="p-3 bg-brand-orange hover:bg-brand-orange/90 text-white rounded-full transition-colors duration-200 font-poppins"
                aria-label={isPlaying ? "Pause" : "Play"}
              >
                {isPlaying ? (
                  <Pause className="w-5 h-5" />
                ) : (
                  <Play className="w-5 h-5" />
                )}
              </button>

              {/* Fast forward button */}
              <button
                onClick={handleFastForward}
                className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Fast forward 10 seconds"
              >
                <FastForward className="w-5 h-5" />
              </button>
            </div>

            {/* Close button */}
            <button
              onClick={handleClosePlayback}
              className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors duration-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Close playback"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Pro Modal */}
      {showProModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl">
            {/* Lock icon with glow effect */}
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-blue-600/30 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Lock className="w-8 h-8 text-blue-300" />
            </div>

            {/* Upgrade messaging */}
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">
              🔒 Obsah pro Pro uživatele
            </h3>

            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 text-center">
              Tento obsah je dostupný pouze s verzí Pro
            </p>

            <div className="text-xs text-gray-500 dark:text-gray-400 mb-6 space-y-1 text-center">
              <div>✓ Všechny kapitoly a testy</div>
              <div>✓ Flashcards + opakování chyb</div>
              <div>✓ Audioknihy (offline poslech později)</div>
            </div>

            {/* Error messages */}
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-600 dark:text-red-200 text-sm">
                {error}
              </div>
            )}

            {/* Action buttons */}
            <div className="space-y-3">
              {user ? (
                // Signed-in user: show upgrade button
                <button
                  onClick={handleCheckout}
                  disabled={checkoutLoading}
                  className="w-full rounded-xl px-4 py-3 font-medium font-poppins bg-brand-orange hover:bg-brand-orange/90 disabled:opacity-60 text-white transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-brand-orange/25"
                >
                  <LoadingButtonContent
                    isLoading={checkoutLoading}
                    loadingText="Otevírám Checkout…"
                    defaultText="🚀 Upgrade na Pro"
                  />
                </button>
              ) : (
                // Signed-out user: show both options
                <>
                  <button
                    onClick={handleCheckout}
                    disabled={checkoutLoading}
                    className="w-full rounded-xl px-4 py-3 font-medium font-poppins bg-brand-orange hover:bg-brand-orange/90 disabled:opacity-60 text-white transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-brand-orange/25"
                  >
                    <LoadingButtonContent
                      isLoading={checkoutLoading}
                      loadingText="Otevírám Checkout…"
                      defaultText="🚀 Upgrade na Pro"
                    />
                  </button>
                  <button
                    onClick={handleSignIn}
                    className="w-full rounded-xl px-4 py-3 font-medium bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white transition-colors border border-gray-200 dark:border-gray-600"
                  >
                    Přihlásit přes Google
                  </button>
                </>
              )}

              {/* Close button */}
              <button
                onClick={() => setShowProModal(false)}
                className="w-full rounded-xl px-4 py-3 font-medium bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors"
              >
                Zavřít
              </button>
            </div>

            {/* Trust indicators */}
            <div className="mt-4 text-xs text-gray-400 text-center">
              🔒 Platba přes Stripe • Bezpečné
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudiobooksSection;